<template>
  <v-dialog
    :value="value"
    :fullscreen="isMobile"
    :hide-overlay="isMobile"
    max-width="650"
    @input="$emit('input', $event)"
  >
    <v-card class="pa-5">
      <v-container class="pa-0 d-flex justify-space-between align-center mb-4">
        <v-card-text class="pa-0 text-h6">
          {{ $t("total-payments") }} {{ $t("invoice") }}
          <span v-if="item && item.number"> #{{ item.number }}</span>
        </v-card-text>
        <v-icon @click="close">mdi-close</v-icon>
      </v-container>

      <v-card-text v-if="item && item.customer" class="pa-0 text-body-2 mb-3">
        {{ $t("customer") }} :
        {{ item.customer.name || $t("haveNot") }}
      </v-card-text>

      <v-container
        class="pa-0 d-flex align-center wrapper-total mb-4"
        :style="{ 'flex-direction': isMobile ? 'column' : '' }"
      >
        <v-container class="item d-flex justify-space-between align-center">
          <v-card-text class="pa-0 text-caption">{{ amountLabel }}</v-card-text>
          <v-card-text class="pa-0 text-caption text-disable secondary--text">
            {{ $numberFormat(invoiceAmount, decimal) }} {{ currencySymbol }}
          </v-card-text>
        </v-container>
        <v-container class="item d-flex justify-space-between align-center">
          <v-card-text class="pa-0 text-caption">{{ $t("total-discount") }}</v-card-text>
          <v-card-text class="pa-0 text-caption text-disable secondary--text">
            {{ $numberFormat(discountAmount, decimal) }} {{ currencySymbol }}
          </v-card-text>
        </v-container>
        <v-container class="item d-flex justify-space-between align-center">
          <v-card-text class="pa-0 text-caption">{{ $t("total-tax") }}</v-card-text>
          <v-card-text class="pa-0 text-caption text-disable secondary--text">
            {{ $numberFormat(taxAmount, decimal) }} {{ currencySymbol }}
          </v-card-text>
        </v-container>
      </v-container>

      <v-data-table
        :headers="headers"
        :items="tableRows"
        :items-per-page="-1"
        hide-default-footer
        dense
        class="payed-details-table"
        :no-data-text="$t('haveNot')"
      >
        <template v-slot:item.row="{ index }">
          <v-card-text class="pa-0 text-caption">{{ index + 1 }}</v-card-text>
        </template>
        <template v-slot:item.type="{ item: row }">
          <v-card-text class="pa-0 text-caption">{{ row.typeLabel }}</v-card-text>
        </template>
        <template v-slot:item.date="{ item: row }">
          <v-card-text
            class="pa-0 text-caption"
            :class="{ 'text-disable secondary--text': !row.date }"
          >
            {{ row.date || $t("haveNot") }}
          </v-card-text>
        </template>
        <template v-slot:item.amount="{ item: row }">
          <v-card-text class="pa-0 text-caption">
            {{ $numberFormat(row.amount, decimal) }} {{ currencySymbol }}
          </v-card-text>
        </template>
        <template v-slot:item.other="{ item: row }">
          <v-card-text class="pa-0 text-caption">
            <div v-if="row.otherLabel">{{ row.otherLabel }}</div>
            <span v-else class="text-disable secondary--text">{{ $t("haveNot") }}</span>
          </v-card-text>
        </template>
      </v-data-table>

      <v-container class="pa-0 d-flex justify-end align-end mt-4">
        <v-card class="pa-3 table-overview" width="250px">
          <div class="d-flex justify-space-between gap-8 mb-2">
            <v-card-text class="pa-0 text-caption">{{ $t("payedPrice") }}</v-card-text>
            <v-card-text class="pa-0 text-caption">
              {{ $numberFormat(paidAmount, decimal) }} {{ currencySymbol }}
            </v-card-text>
          </div>
          <div class="d-flex justify-space-between gap-8 align-center">
            <v-card-text class="pa-0 text-caption">{{ $t("remainPrice") }}</v-card-text>
            <v-chip
              small
              outlined
              :color="remainAmount > 0 ? 'red' : 'green'"
              class="px-2"
            >
              {{
                remainAmount > 0
                  ? $numberFormat(remainAmount, decimal) + " " + currencySymbol
                  : $t("payed")
              }}
            </v-chip>
          </div>
        </v-card>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "PayedDetailsDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      default: null,
    },
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.name === "xs";
    },
    currencySymbol() {
      return this.item && this.item.currency ? this.item.currency.symbol : "";
    },
    decimal() {
      return this.item && this.item.currency ? this.item.currency.decimal : 0;
    },
    isReceive() {
      const type = this.item && this.item.type;
      return type === "sell" || type === "returnBuy" || type === "recieve";
    },
    amountLabel() {
      return this.isReceive ? this.$t("amountForRecieve") : this.$t("amountForPay");
    },
    invoiceAmount() {
      if (!this.item || !this.item.price) return 0;
      return parseFloat(this.item.price.price || 0);
    },
    discountAmount() {
      return parseFloat((this.item && this.item.discount) || 0);
    },
    taxAmount() {
      return parseFloat((this.item && this.item.tax) || 0);
    },
    paidAmount() {
      if (!this.item || !this.item.payed) return 0;
      return parseFloat(this.item.payed.amount || 0);
    },
    remainAmount() {
      if (!this.item || !this.item.price) return 0;
      const price = parseFloat(this.item.price.price || 0);
      const paid = this.paidAmount;
      const credit = parseFloat(this.item.credit || 0);
      return price - paid - credit;
    },
    headers() {
      return [
        {
          text: this.$t("#"),
          align: "center",
          sortable: false,
          value: "row",
          width: "28px",
        },
        {
          text: this.$t("type"),
          align: "start",
          sortable: false,
          value: "type",
          width: "64px",
        },
        {
          text: this.$t("date"),
          align: "start",
          sortable: false,
          value: "date",
          width: "96px",
        },
        {
          text: this.$t("amount"),
          align: "start",
          sortable: false,
          value: "amount",
          width: "120px",
        },
        {
          text: this.$t("other"),
          align: "start",
          sortable: false,
          value: "other",
        },
      ];
    },
    tableRows() {
      const rows = [];

      const payments = (this.item && this.item.payments) || [];
      payments.forEach((entry) => {
        const payment = entry.payments || entry;
        if (!payment) return;
        const bank = payment.bank || {};
        const bankLabel = bank.name
          ? `${this.$t("bank")} : ${bank.name}`
          : this.$t("haveNot");
        rows.push({
          typeLabel: this.resolvePaymentTypeLabel(payment, bank),
          date: this.formatDateOnly(payment.date),
          amount: parseFloat(payment.amount || 0),
          otherLabel: bankLabel,
        });
      });

      const checks = (this.item && this.item.checks) || [];
      checks.forEach((entry) => {
        const check = entry.checks || entry;
        if (!check) return;
        const bankName =
          (check.bank && check.bank.name) ||
          check.bank_name ||
          check.bankName ||
          null;
        const fishermanNumber =
          check.fisherman_number || check.fishermanNumber || null;
        const otherParts = [];
        if (bankName) {
          otherParts.push(`${this.$t("bank")} : ${bankName}`);
        }
        if (check.number) {
          otherParts.push(`${this.$t("check-number")} : ${check.number}`);
        }
        if (fishermanNumber) {
          otherParts.push(`${this.$t("fishermanNumber")} : ${fishermanNumber}`);
        }
        rows.push({
          typeLabel: this.$t("check"),
          date: this.formatDateOnly(
            check.date || check.due_date || check.dueDate || check.time
          ),
          amount: parseFloat(check.amount || 0),
          otherLabel: otherParts.length ? otherParts.join(" | ") : this.$t("haveNot"),
        });
      });

      return rows;
    },
  },
  methods: {
    close() {
      this.$emit("input", false);
    },
    formatDateOnly(value) {
      if (!value) return null;
      const text = String(value).trim();
      // "۱۳ مهر ۱۴۰۵ - ۱۰:۴۷" or "13 مهر 1405 - 10:47"
      const dashSplit = text.split(/\s+-\s+/);
      if (dashSplit.length > 1 && /[:\d۰-۹]/.test(dashSplit[dashSplit.length - 1])) {
        return dashSplit.slice(0, -1).join(" - ").trim();
      }
      // "1405/07/13 10:47"
      return text.replace(/\s+\d{1,2}:\d{2}(:\d{2})?$/, "").trim();
    },
    resolvePaymentTypeLabel(payment, bank) {
      if (bank && bank.type === "cash") {
        return this.$t("cash-format");
      }
      if (payment && payment.type) {
        return this.$t(payment.type);
      }
      return this.$t("cash");
    },
  },
};
</script>

<style scoped>
.wrapper-total {
  border: 1px solid silver;
  border-radius: 4px;
}
.wrapper-total .item {
  border-left: 1px solid silver;
  padding: 5px !important;
}
.wrapper-total .item:last-child {
  border-left: 0;
}
.v-card__text {
  width: auto;
}
.table-overview {
  border: 1px solid silver;
  box-shadow: none !important;
}
::v-deep .v-data-table.payed-details-table {
  border: 1px solid #d0d7de;
  border-radius: 4px;
  overflow: hidden;
  background: #fff;
}
::v-deep .v-data-table.payed-details-table .v-data-table__wrapper {
  overflow: hidden !important;
}
::v-deep .v-data-table.payed-details-table > .v-data-table__wrapper > table {
  display: block;
  width: 100%;
}
::v-deep
  .v-data-table.payed-details-table
  > .v-data-table__wrapper
  > table
  > thead {
  display: table;
  width: 100%;
  table-layout: fixed;
}
::v-deep
  .v-data-table.payed-details-table
  > .v-data-table__wrapper
  > table
  > tbody {
  display: block;
  max-height: 220px;
  overflow-y: auto;
  width: 100%;
}
::v-deep
  .v-data-table.payed-details-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr {
  display: table;
  width: 100%;
  table-layout: fixed;
}
::v-deep
  .v-data-table.payed-details-table
  > .v-data-table__wrapper
  > table
  > tbody::-webkit-scrollbar {
  width: 5px;
}
::v-deep
  .v-data-table.payed-details-table
  > .v-data-table__wrapper
  > table
  > tbody::-webkit-scrollbar-thumb {
  background-color: rgba(148, 163, 184, 0.45);
  border-radius: 8px;
}
::v-deep
  .v-data-table.payed-details-table
  > .v-data-table__wrapper
  > table
  > tbody::-webkit-scrollbar-thumb:hover {
  background-color: rgba(148, 163, 184, 0.7);
}
::v-deep
  .v-data-table.payed-details-table
  > .v-data-table__wrapper
  > table
  > tbody::-webkit-scrollbar-track {
  background-color: transparent;
}
::v-deep .v-data-table.payed-details-table .v-data-table-header,
::v-deep .v-data-table.payed-details-table .v-data-table-header tr,
::v-deep
  .v-data-table.payed-details-table
  > .v-data-table__wrapper
  > table
  > thead
  > tr
  > th {
  background-color: #f3f6f9 !important;
}
::v-deep
  .v-data-table.payed-details-table
  > .v-data-table__wrapper
  > table
  > thead
  > tr
  > th {
  border-left: 1px solid #d5dde6;
  border-bottom: 1px solid #d0d8e2;
  font-size: 11px;
  font-weight: 600;
  color: #4b5563;
  height: 32px !important;
  padding: 0 8px;
  letter-spacing: 0;
}
::v-deep
  .v-data-table.payed-details-table
  > .v-data-table__wrapper
  > table
  > thead
  > tr
  > th:last-child {
  border-left: none;
}
::v-deep
  .v-data-table.payed-details-table
  > .v-data-table__wrapper
  > table
  > thead
  > tr
  > th:first-child,
::v-deep
  .v-data-table.payed-details-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr
  > td:first-child {
  width: 28px !important;
  max-width: 28px !important;
  min-width: 28px !important;
  padding-left: 2px !important;
  padding-right: 2px !important;
  text-align: center;
}
::v-deep
  .v-data-table.payed-details-table
  > .v-data-table__wrapper
  > table
  > thead
  > tr
  > th:nth-child(2),
::v-deep
  .v-data-table.payed-details-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr
  > td:nth-child(2) {
  width: 64px !important;
  max-width: 64px !important;
  min-width: 64px !important;
  white-space: nowrap;
}
::v-deep
  .v-data-table.payed-details-table
  > .v-data-table__wrapper
  > table
  > thead
  > tr
  > th:nth-child(3),
::v-deep
  .v-data-table.payed-details-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr
  > td:nth-child(3) {
  width: 96px !important;
  max-width: 96px !important;
  min-width: 96px !important;
  white-space: nowrap;
}
::v-deep
  .v-data-table.payed-details-table
  > .v-data-table__wrapper
  > table
  > thead
  > tr
  > th:nth-child(4),
::v-deep
  .v-data-table.payed-details-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr
  > td:nth-child(4) {
  width: 120px !important;
  max-width: 120px !important;
  min-width: 120px !important;
  white-space: nowrap;
}
::v-deep
  .v-data-table.payed-details-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr
  > td {
  border-left: 1px solid #e5eaf0;
  border-bottom: 1px solid #e5eaf0;
  font-size: 12px;
  color: #1f2937;
  height: 44px;
  padding-right: 10px;
  background: #fff;
}
::v-deep
  .v-data-table.payed-details-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr:nth-child(even)
  > td {
  background: #f8fafc;
}
::v-deep
  .v-data-table.payed-details-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr:hover
  > td {
  background: #eef6ff;
}
::v-deep
  .v-data-table.payed-details-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr
  > td:last-child {
  border-left: none;
}
::v-deep
  .v-data-table.payed-details-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr:last-child
  > td {
  border-bottom: 1px solid #e5eaf0 !important;
}
</style>
